/**
 * sheetsClient.ts — VIREON Cortex data fetcher (v6 — single-tab GViz CSV)
 * ==========================================================================
 *
 * DATA MODEL (v6):
 *   Every meter writes ONE flat tab per sheet. Each row is:
 *     Timestamp, <raw register columns>, <Pi gateway columns>
 *   The LK4410 contributes ~102 register columns, the LK4405 ~42, and both
 *   append the Raspberry Pi health columns (PiTempC, PiCpuPct, …) to the same
 *   row. There is no longer a separate METADATA tab — gateway fields are read
 *   from the same flat row by header name.
 *
 *   Columns are matched BY HEADER NAME through the row's MeterModel
 *   (src/meters), so the pipeline is robust to column reordering or inserted
 *   columns. The model is taken from SheetSource.meterModel when declared, else
 *   auto-detected from the header row.
 *
 *   State/tariff is independent of the meter model: an LK4410 in West Bengal
 *   parses as LK4410 and bills as WBSEDCL.
 *
 * REMOVED IN v6:
 *   The legacy two-tab METADATA + RAW "split format" path (and its access-token
 *   parameter) — no live sheet uses it. All sheets are single-tab.
 */

import type { RawMeterRow, MetaRow, TelemetryRow } from '@/types';
import type { SheetSource } from '@/config/sheets';
import type { MeterModelCode } from '@/meters';
import { resolveMeterModel, buildHeaderIndex, buildRawRowFromHeaders } from '@/meters';

import { computeAllDerived } from '@/lib/derivedEngine';
import { getTariffForSite } from '@/tariffs';

export interface FetchResult {
  rows: TelemetryRow[];
  fetchedAt: Date;
  counts: { rows: number };
  /** Which meter model was used to parse this sheet (for diagnostics/UI). */
  meterModel: MeterModelCode;
}

// ─── CSV parser ───────────────────────────────────────────────────────────────

function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cell += '"'; i++; }
        else inQuotes = false;
      } else {
        cell += ch;
      }
    } else {
      if      (ch === '"')  { inQuotes = true; }
      else if (ch === ',')  { row.push(cell); cell = ''; }
      else if (ch === '\n') {
        row.push(cell); cell = '';
        if (row.some(c => c.trim())) rows.push(row);
        row = [];
      } else if (ch === '\r') { /* skip */ }
      else { cell += ch; }
    }
  }
  if (cell.trim() || row.length) {
    row.push(cell);
    if (row.some(c => c.trim())) rows.push(row);
  }
  return rows;
}

// ─── GViz fetch (returns header + data separately) ────────────────────────────

interface SheetTable { header: string[]; rows: string[][]; }

async function fetchGvizTable(sheetId: string, gidOrTab: string | number): Promise<SheetTable> {
  const param = typeof gidOrTab === 'number' || /^\d+$/.test(String(gidOrTab))
    ? `gid=${gidOrTab}`
    : `sheet=${encodeURIComponent(String(gidOrTab))}`;
  const url = `https://docs.google.com/spreadsheets/d/${sheetId}/gviz/tq?tqx=out:csv&${param}&t=${Date.now()}`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`GViz fetch failed (${gidOrTab}): HTTP ${res.status}`);

  const text = await res.text();
  if (text.trimStart().startsWith('<!')) {
    throw new Error(`Sheet "${gidOrTab}" returned HTML — make sure the sheet is shared as "Anyone with the link can view".`);
  }
  const all = parseCSV(text);
  const header = all.length ? all[0] : [];
  const rows = all.slice(1);
  return { header, rows };
}

// ─── Timestamp helpers ────────────────────────────────────────────────────────

function excelSerialToISO(serial: number): string {
  const d = new Date(new Date(1899, 11, 30).getTime() + serial * 86_400_000);
  return d.toISOString().slice(0, 19).replace('T', ' ');
}

function normalizeTimestamp(ts: unknown): string {
  if (typeof ts === 'number') return excelSerialToISO(ts);
  let str = String(ts).trim();
  if (str.startsWith("'")) str = str.slice(1).trim();
  str = str.replace('T', ' ');
  return str.split('.')[0];
}

// Safari/WebKit requires ISO 'T' separator with 2-digit HH:mm:ss components.
// Single-digit hours (e.g. "8:40:48") cause new Date("2026-07-29T8:40:48") to return NaN.
function parseTimestamp(normalized: string): Date {
  let str = String(normalized).trim();
  if (str.startsWith("'")) str = str.slice(1).trim();

  const parts = str.split(/[T ]+/);
  if (parts.length >= 2) {
    const datePart = parts[0];
    const timePart = parts[1];

    const dParts = datePart.split('-').map(p => p.padStart(2, '0'));
    const tParts = timePart.split(':').map(p => p.padStart(2, '0'));

    if (dParts.length === 3 && tParts.length >= 2) {
      const formatted = `${dParts[0]}-${dParts[1]}-${dParts[2]}T${tParts[0]}:${tParts[1]}:${tParts[2] || '00'}`;
      const d = new Date(formatted);
      if (!Number.isNaN(d.getTime())) return d;
    }
  }

  const iso = str.includes('T') ? str : str.replace(' ', 'T');
  return new Date(iso);
}

// ─── Number/bool helpers (for flat-tab Pi gateway columns) ────────────────────

const num = (v: unknown, def = 0): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};
const bool = (v: unknown): boolean => String(v).toLowerCase() === 'true';

// ─── MetaRow fallback ─────────────────────────────────────────────────────────

function fallbackMetaRow(ts: Date): MetaRow {
  const y  = ts.getFullYear();
  const mo = String(ts.getMonth() + 1).padStart(2, '0');
  const d  = String(ts.getDate()).padStart(2, '0');
  const h  = String(ts.getHours()).padStart(2, '0');
  const mi = String(ts.getMinutes()).padStart(2, '0');
  const s  = String(ts.getSeconds()).padStart(2, '0');
  return {
    date: `${y}-${mo}-${d}`, time: `${h}:${mi}:${s}`, hour: ts.getHours(),
    deviceId: '', location: '', meterSerial: '', modelNumber: '',
    piTempC: 0, piCpuPct: 0, piMemPct: 0, piDiskPct: 0, piUptimeHrs: 0,
    piThermalThrottle: false, piDiskFull: false,
  };
}

function buildFlatMetaRow(headerIndex: Map<string, number>, cols: string[], ts: Date): MetaRow {
  const meta = fallbackMetaRow(ts);
  const cell = (header: string): unknown => {
    const idx = headerIndex.get(header.toLowerCase());
    return idx === undefined ? undefined : cols[idx];
  };

  return {
    ...meta,
    piTempC:           num(cell('PiTempC'), meta.piTempC),
    piCpuPct:          num(cell('PiCpuPct'), meta.piCpuPct),
    piMemPct:          num(cell('PiMemPct'), meta.piMemPct),
    piDiskPct:         num(cell('PiDiskPct'), meta.piDiskPct),
    piUptimeHrs:       num(cell('PiUptimeHrs'), meta.piUptimeHrs),
    piThermalThrottle: bool(cell('PiThrottled')),
    piDiskFull:        bool(cell('PiDiskFull')),
  };
}

// ─── Main export ──────────────────────────────────────────────────────────────

/**
 * Fetch and parse ONE flat tab into joined raw+meta rows. Raw register columns
 * AND the Pi gateway columns live in this one tab and are matched by header
 * name. Falls back to the first/default sheet (gid 0) when no gid is given.
 */
async function loadTab(
  sheetId: string,
  gid: string | number | undefined,
  declaredModel: MeterModelCode | undefined,
): Promise<{ joined: (RawMeterRow & MetaRow)[]; modelCode: MeterModelCode }> {
  const tab: string | number =
    gid !== undefined && gid !== null && gid !== '' ? gid : 0;

  const { header, rows: rawRows } = await fetchGvizTable(sheetId, tab);

  const model = resolveMeterModel(declaredModel, header);
  const headerIndex = buildHeaderIndex(header);

  // Timestamp column: prefer a header literally called "timestamp", else col 0.
  const tsIdx = headerIndex.get('timestamp') ?? 0;

  const joined: (RawMeterRow & MetaRow)[] = [];
  for (const cols of rawRows) {
    const tsNorm = normalizeTimestamp(cols[tsIdx]);
    const ts     = parseTimestamp(tsNorm);
    if (Number.isNaN(ts.getTime())) continue;
    const raw  = buildRawRowFromHeaders(model, headerIndex, cols, ts);
    const meta = buildFlatMetaRow(headerIndex, cols, ts);
    joined.push({ ...raw, ...meta, timestamp: ts });
  }
  return { joined, modelCode: model.code };
}

// Legacy sheets hold immutable history (rows before a gateway-cutover date that
// never change), yet fetchTelemetry runs on every poll. Cache the parsed legacy
// rows per (sheet, gid, model) so we hit Google Sheets ONCE, not every refresh —
// this both saves bandwidth and avoids GViz HTTP 429 rate-limiting. Failures are
// not cached, so a throttled/failed legacy fetch is retried on the next poll.
const legacyCache = new Map<string, Promise<(RawMeterRow & MetaRow)[]>>();

function loadLegacyCached(
  sheetId: string,
  gid: string | number | undefined,
  model: MeterModelCode | undefined,
): Promise<(RawMeterRow & MetaRow)[]> {
  const key = `${sheetId}|${gid ?? ''}|${model ?? ''}`;
  let p = legacyCache.get(key);
  if (!p) {
    p = loadTab(sheetId, gid, model).then(r => r.joined);
    p.catch(() => legacyCache.delete(key)); // don't cache a failure — allow retry
    legacyCache.set(key, p);
  }
  return p;
}

export async function fetchTelemetry(
  source: SheetSource,
): Promise<FetchResult> {
  const gid = (source as SheetSource & { gid?: string | number }).gid;
  const declaredModel = (source as SheetSource & { meterModel?: MeterModelCode }).meterModel;

  const tariffForCd = getTariffForSite(source.stateCode, source.meta?.emissionFactorKgPerKwh);
  const contractDemandKva = source.meta?.contractDemandKva ?? tariffForCd.defaultContractDemandKva;

  // Legacy source (gateway cutover): rows before `until` come from the old
  // sheet, rows from `until` onward from the primary. Cached (immutable history)
  // so it isn't re-fetched every poll. A fetch failure degrades to live-only.
  const legacyPromise = source.legacy
    ? loadLegacyCached(source.legacy.sheetId, source.legacy.gid, source.legacy.meterModel)
        .catch(err => {
          console.warn('⚠️ Legacy sheet fetch failed — showing live data only:', err);
          return [] as (RawMeterRow & MetaRow)[];
        })
    : Promise.resolve([] as (RawMeterRow & MetaRow)[]);

  const [primary, legacyRows] = await Promise.all([
    loadTab(source.sheetId, gid, declaredModel),
    legacyPromise,
  ]);
  const usedModelCode = primary.modelCode;

  let joined = primary.joined;
  if (source.legacy) {
    const cutoff = parseTimestamp(source.legacy.until).getTime();
    joined = [
      ...legacyRows.filter(r => r.timestamp.getTime() < cutoff),
      ...joined.filter(r => r.timestamp.getTime() >= cutoff),
    ];
  }

  // Resolve the tariff for this meter's state so ToD bucketing, energy rates,
  // the billing register (kWh vs kVAh), demand/ED/fixed charges all match.
  const rows = computeAllDerived(joined, contractDemandKva, tariffForCd);

  return {
    rows,
    fetchedAt: new Date(),
    counts: { rows: rows.length },
    meterModel: usedModelCode,
  };
}

// Re-export so consumers that want the model registry don't reach past the client.
export { getMeterModel } from '@/meters';
